import React, { useState, useEffect, useCallback, Component } from 'react';
import {
  Box, Typography, Tabs, Tab, Paper, Grid, Card, CardContent,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Select, MenuItem, FormControl, InputLabel, Button, Chip,
  CircularProgress, Divider, Tooltip, IconButton,
} from '@mui/material';
import {
  FileDownload, Inventory2, CurrencyExchange, AccountBalance,
  BarChart as BarChartIcon, TableChart, TrendingUp,
  Groups, CalendarMonth, Summarize, Dashboard as DashboardIcon,
  Receipt as ReceiptIcon, ShowChart, Pending, ArrowUpward, ArrowDownward,
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
  ResponsiveContainer, Cell, Legend,
  LineChart, Line, PieChart, Pie, ComposedChart, Area,
} from 'recharts';
import * as XLSX from 'xlsx';
import api from '../../services/api';

// ── Error boundary to catch render crashes ────────────────────────────────────
class TabErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) { console.error('Reports tab crash:', error, info); }
  render() {
    if (this.state.error) {
      return (
        <Box sx={{ p: 3 }}>
          <Typography color="error" variant="subtitle1" fontWeight={700}>Something went wrong in this tab.</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontFamily: 'monospace', fontSize: 12 }}>
            {this.state.error?.message || String(this.state.error)}
          </Typography>
          <Button sx={{ mt: 2 }} variant="outlined" size="small"
            onClick={() => this.setState({ error: null })}>Retry</Button>
        </Box>
      );
    }
    return this.props.children;
  }
}

// ── helpers ──────────────────────────────────────────────────────────────────
const INR = (v) => v == null ? '—' : '₹' + Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const fmtDate = (d) => !d ? null : typeof d === 'string' ? d.substring(0, 10) : (d instanceof Date ? d.toISOString().substring(0, 10) : String(d).substring(0, 10));
const safeNum = (v) => v == null ? null : parseFloat(v);
const USD = (v) => v == null ? '—' : '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const NUM = (v) => v == null ? '—' : Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 });

const CURRENT_YEAR  = new Date().getFullYear();
const CURRENT_MONTH = new Date().getMonth() + 1;
const MONTH_NAMES   = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                            'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_FULL    = ['', 'January', 'February', 'March', 'April', 'May', 'June',
                           'July', 'August', 'September', 'October', 'November', 'December'];
const YEARS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - i);

// Excel export helper
function exportExcel(rows, sheetName, filename) {
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, filename);
}

// Summary stat card
function StatCard({ icon, label, value, color = 'primary.main', sub }) {
  return (
    <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, p: 2.5 }}>
        <Box sx={{ color, mt: 0.3, flexShrink: 0 }}>{icon}</Box>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {label}
          </Typography>
          <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.2 }}>{value}</Typography>
          {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
        </Box>
      </CardContent>
    </Card>
  );
}

// Month + Year pickers
function MonthYearPicker({ year, month, onYear, onMonth, availableYears }) {
  const yrs = (availableYears?.length ? availableYears : YEARS);
  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
      <FormControl size="small" sx={{ minWidth: 110 }}>
        <InputLabel>Year</InputLabel>
        <Select value={year} label="Year" onChange={e => onYear(e.target.value)}>
          {yrs.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 140 }}>
        <InputLabel>Month</InputLabel>
        <Select value={month} label="Month" onChange={e => onMonth(e.target.value)}>
          {MONTH_FULL.slice(1).map((m, i) => (
            <MenuItem key={i+1} value={i+1}>{m}</MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
}

// Year-only picker
function YearPicker({ year, onYear, availableYears }) {
  const yrs = (availableYears?.length ? availableYears : YEARS);
  return (
    <FormControl size="small" sx={{ minWidth: 110, mb: 3 }}>
      <InputLabel>Year</InputLabel>
      <Select value={year} label="Year" onChange={e => onYear(e.target.value)}>
        {yrs.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
      </Select>
    </FormControl>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TAB 1 – BOE Monthly Report
// ════════════════════════════════════════════════════════════════════════════
function BOEMonthlyReport() {
  const [year,    setYear]    = useState(CURRENT_YEAR);
  const [month,   setMonth]   = useState(CURRENT_MONTH);
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [availableYears, setAvailableYears] = useState([]);

  // On mount, fetch available years and default to the most recent one with data
  useEffect(() => {
    api.get('/reports2/hboe/yearly?year=' + CURRENT_YEAR)
      .then(r => {
        const yrs = r.data.available_years || [];
        if (yrs.length) {
          setAvailableYears(yrs);
          if (!yrs.includes(CURRENT_YEAR)) setYear(yrs[0]);
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/reports2/hboe/monthly?year=${year}&month=${month}`);
      setData(r.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [year, month]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExport = () => {
    if (!data?.data?.length) return;
    const rows = data.data.map((r, i) => ({
      'Sr.No.':           i + 1,
      'BOE Number':       r.boe_number || r.cbe_number || '',
      'BOE Date':         fmtDate(r.boe_date || r.ooc_date) || '',
      'HAWB Number':      r.hawb_number || '',
      'Supplier':         r.consignor_name || '',
      'Invoice No.':      r.invoice_number || '',
      [`Invoice Value (${r.invoice_currency || 'USD'})`]: r.invoice_value || 0,
      'Exch. Rate':       r.exchange_rate || 0,
      'Assessable Value (INR)': r.assessable_value || 0,
      'IGST (INR)':       r.igst_amount || 0,
      'Total Duty (INR)': r.duty_amount || 0,
      'HS Code':          r.hs_code || '',
      'Description':      r.description_of_goods || '',
      'Qty':              r.quantity || '',
      'UOM':              r.unit_of_measure || '',
    }));
    exportExcel(rows, 'BOE Monthly', `BOE_Report_${MONTH_FULL[month]}_${year}.xlsx`);
  };

  const s   = data?.summary  || {};
  const boeRows = data?.data || [];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <MonthYearPicker year={year} month={month} onYear={setYear} onMonth={setMonth} availableYears={availableYears} />
        <Button variant="contained" startIcon={<FileDownload />} onClick={handleExport} disabled={!boeRows.length}>
          Export Excel
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : data ? (
        <>
          {/* Summary cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <StatCard icon={<Inventory2 />} label="Total BOEs" value={s.count ?? 0} color="primary.main" />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard icon={<CurrencyExchange />} label="Invoice Value (USD)" value={USD(s.total_invoice_usd)} color="success.main" />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard icon={<AccountBalance />} label="Assessable Value (INR)" value={INR(s.total_assessable_inr)} color="warning.main" />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard icon={<TrendingUp />} label="Total IGST (INR)" value={INR(s.total_igst)} color="error.main"
                sub={`Total Duty: ${INR(s.total_duty)}`} />
            </Grid>
          </Grid>

          {/* Table */}
          {boeRows.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
              <Typography color="text.secondary">No BOE records found for {MONTH_FULL[month]} {year}.</Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.50' } }}>
                    <TableCell>#</TableCell>
                    <TableCell>BOE Number</TableCell>
                    <TableCell>BOE Date</TableCell>
                    <TableCell>Supplier</TableCell>
                    <TableCell>Invoice No.</TableCell>
                    <TableCell align="right">Invoice Value</TableCell>
                    <TableCell align="right">Rate</TableCell>
                    <TableCell align="right">Assessable (INR)</TableCell>
                    <TableCell align="right">IGST (INR)</TableCell>
                    <TableCell align="right">Total Duty (INR)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {boeRows.map((r, i) => (
                    <TableRow key={r.id} hover sx={{ '&:last-child td': { border: 0 } }}>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>{i + 1}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 220 }}>
                          {r.boe_number || r.cbe_number || '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{r.hawb_number}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{fmtDate(r.boe_date || r.ooc_date)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={r.consignor_name || ''}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>
                            {r.consignor_name || '—'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>
                          {r.invoice_number || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          {r.invoice_value != null
                            ? `${r.invoice_currency === 'USD' ? '$' : '¥'}${Number(r.invoice_value).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
                            : '—'}
                        </Typography>
                        {r.invoice_currency && r.invoice_currency !== 'USD' && (
                          <Chip label={r.invoice_currency} size="small" sx={{ fontSize: '0.65rem', height: 16, ml: 0.5 }} />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">{r.exchange_rate != null ? parseFloat(r.exchange_rate).toFixed(2) : '—'}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">{INR(r.assessable_value)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600} color="error.main">
                          {INR(r.igst_amount)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">{INR(r.duty_amount)}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Totals row */}
                  <TableRow sx={{ bgcolor: '#e3f2fd', '& td': { fontWeight: 700, borderTop: '2px solid #90caf9' } }}>
                    <TableCell colSpan={5} align="right">TOTAL ({boeRows.length} BOEs)</TableCell>
                    <TableCell align="right">{USD(s.total_invoice_usd)}</TableCell>
                    <TableCell />
                    <TableCell align="right">{INR(s.total_assessable_inr)}</TableCell>
                    <TableCell align="right" sx={{ color: 'error.main' }}>{INR(s.total_igst)}</TableCell>
                    <TableCell align="right">{INR(s.total_duty)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      ) : null}
    </Box>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TAB 2 – BOE Yearly Overview
// ════════════════════════════════════════════════════════════════════════════
function BOEYearlyOverview() {
  const [year,    setYear]    = useState(CURRENT_YEAR);
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/reports2/hboe/yearly?year=${year}`);
      setData(r.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [year]);

  useEffect(() => { fetch(); }, [fetch]);

  const handleExport = () => {
    if (!data?.months) return;
    const rows = data.months.filter(m => m.count > 0).map(m => ({
      'Month':               m.month_name,
      'BOE Count':           m.count,
      'Invoice Value (USD)': m.total_usd || 0,
      'Assessable (INR)':    m.total_assessable || 0,
      'IGST (INR)':          m.total_igst || 0,
      'Total Duty (INR)':    m.total_duty || 0,
    }));
    exportExcel(rows, `BOE ${year}`, `BOE_Yearly_${year}.xlsx`);
  };

  const chartData = data?.months?.map(m => ({
    name: m.month_name,
    Assessable: Math.round(m.total_assessable || 0),
    IGST:       Math.round(m.total_igst || 0),
    count:      m.count,
  })) || [];

  const BAR_COLORS = ['#1976d2', '#d32f2f'];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <YearPicker year={year} onYear={setYear} availableYears={data?.available_years} />
        <Button variant="contained" startIcon={<FileDownload />} onClick={handleExport} disabled={!data?.months}>
          Export Excel
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : data ? (
        <>
          {/* Summary cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <StatCard icon={<Inventory2 />} label="Total BOEs" value={data?.totals?.count ?? 0} color="primary.main" />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard icon={<CurrencyExchange />} label="Total Invoice (USD)" value={USD(data?.totals?.total_usd)} color="success.main" />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard icon={<AccountBalance />} label="Total Assessable (INR)" value={INR(data?.totals?.total_assessable)} color="warning.main" />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard icon={<TrendingUp />} label="Total IGST (INR)" value={INR(data?.totals?.total_igst)} color="error.main"
                sub={`Total Duty: ${INR(data?.totals?.total_duty)}`} />
            </Grid>
          </Grid>

          {/* Bar chart */}
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={700} mb={2}>Monthly Assessable Value vs IGST — {year}</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis tickFormatter={v => `₹${(v/100000).toFixed(0)}L`} />
                <ReTooltip formatter={(v, name) => [INR(v), name]} />
                <Legend />
                <Bar dataKey="Assessable" fill={BAR_COLORS[0]} radius={[4, 4, 0, 0]} />
                <Bar dataKey="IGST"       fill={BAR_COLORS[1]} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>

          {/* Monthly table */}
          <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.50' } }}>
                  <TableCell>Month</TableCell>
                  <TableCell align="center">BOE Count</TableCell>
                  <TableCell align="right">Invoice (USD)</TableCell>
                  <TableCell align="right">Assessable (INR)</TableCell>
                  <TableCell align="right">IGST (INR)</TableCell>
                  <TableCell align="right">Total Duty (INR)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data?.months || []).map(m => (
                  <TableRow key={m.month_num} hover
                    sx={{ opacity: m.count === 0 ? 0.4 : 1, '&:last-child td': { border: 0 } }}>
                    <TableCell sx={{ fontWeight: 600 }}>{MONTH_FULL[m.month_num]}</TableCell>
                    <TableCell align="center">
                      {m.count > 0
                        ? <Chip label={m.count} size="small" color="primary" variant="outlined" />
                        : <Typography variant="body2" color="text.disabled">—</Typography>}
                    </TableCell>
                    <TableCell align="right">{m.count > 0 ? USD(m.total_usd) : '—'}</TableCell>
                    <TableCell align="right">{m.count > 0 ? INR(m.total_assessable) : '—'}</TableCell>
                    <TableCell align="right" sx={{ color: m.count > 0 ? 'error.main' : undefined }}>
                      {m.count > 0 ? INR(m.total_igst) : '—'}
                    </TableCell>
                    <TableCell align="right">{m.count > 0 ? INR(m.total_duty) : '—'}</TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: '#e3f2fd', '& td': { fontWeight: 700, borderTop: '2px solid #90caf9' } }}>
                  <TableCell>TOTAL {year}</TableCell>
                  <TableCell align="center">{data?.totals?.count ?? 0}</TableCell>
                  <TableCell align="right">{USD(data?.totals?.total_usd)}</TableCell>
                  <TableCell align="right">{INR(data?.totals?.total_assessable)}</TableCell>
                  <TableCell align="right" sx={{ color: 'error.main' }}>{INR(data?.totals?.total_igst)}</TableCell>
                  <TableCell align="right">{INR(data?.totals?.total_duty)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : null}
    </Box>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TAB 3 – Supplier-wise BOE Analysis
// ════════════════════════════════════════════════════════════════════════════
function SupplierAnalysis() {
  const [year,    setYear]    = useState('');
  const [month,   setMonth]   = useState('');
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [availableYears, setAvailableYears] = useState([]);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (year)  params.append('year', year);
      if (month) params.append('month', month);
      const r = await api.get(`/reports2/hboe/suppliers?${params}`);
      setData(r.data);
      if (r.data.available_years?.length) setAvailableYears(r.data.available_years);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [year, month]);

  useEffect(() => { fetch(); }, [fetch]);

  const handleExport = () => {
    if (!data?.data?.length) return;
    const rows = data.data.map((r, i) => ({
      'Sr.No.': i + 1,
      'Supplier / Consignor': r.supplier,
      'BOE Count':     r.boe_count,
      'Invoice (USD)': r.total_usd || 0,
      'Assessable (INR)': r.total_assessable || 0,
      'IGST (INR)':    r.total_igst || 0,
      'Total Duty (INR)': r.total_duty || 0,
      'First Import':  r.first_import?.substring(0, 10),
      'Last Import':   r.last_import?.substring(0, 10),
    }));
    exportExcel(rows, 'Supplier Analysis', `BOE_Supplier_Analysis${year ? `_${year}` : ''}.xlsx`);
  };

  const yrs = availableYears.length ? availableYears : YEARS;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>Year</InputLabel>
            <Select value={year} label="Year" onChange={e => setYear(e.target.value)}>
              <MenuItem value="">All Years</MenuItem>
              {yrs.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Month</InputLabel>
            <Select value={month} label="Month" onChange={e => setMonth(e.target.value)}>
              <MenuItem value="">All Months</MenuItem>
              {MONTH_FULL.slice(1).map((m, i) => (
                <MenuItem key={i+1} value={i+1}>{m}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Button variant="contained" startIcon={<FileDownload />} onClick={handleExport} disabled={!data?.data?.length}>
          Export Excel
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : data ? (
        <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.50' } }}>
                <TableCell>#</TableCell>
                <TableCell>Supplier / Consignor</TableCell>
                <TableCell align="center">BOE Count</TableCell>
                <TableCell align="right">Invoice Value (USD)</TableCell>
                <TableCell align="right">Assessable (INR)</TableCell>
                <TableCell align="right">IGST (INR)</TableCell>
                <TableCell align="right">Total Duty (INR)</TableCell>
                <TableCell>Date Range</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(data?.data || []).map((r, i) => (
                <TableRow key={i} hover sx={{ '&:last-child td': { border: 0 } }}>
                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>{i + 1}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{r.supplier}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={r.boe_count} size="small" color="primary" variant="outlined" />
                  </TableCell>
                  <TableCell align="right">{USD(r.total_usd)}</TableCell>
                  <TableCell align="right">{INR(r.total_assessable)}</TableCell>
                  <TableCell align="right" sx={{ color: 'error.main', fontWeight: 600 }}>{INR(r.total_igst)}</TableCell>
                  <TableCell align="right">{INR(r.total_duty)}</TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {r.first_import?.substring(0, 10)} → {r.last_import?.substring(0, 10)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}
    </Box>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TAB 4 – Remittance Monthly Report
// ════════════════════════════════════════════════════════════════════════════
function RemittanceMonthlyReport() {
  const [year,    setYear]    = useState(CURRENT_YEAR);
  const [month,   setMonth]   = useState(CURRENT_MONTH);
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [availableYears, setAvailableYears] = useState([]);

  useEffect(() => {
    api.get('/reports2/remittances/yearly?year=' + CURRENT_YEAR)
      .then(r => {
        const yrs = r.data.available_years || [];
        if (yrs.length) {
          setAvailableYears(yrs);
          if (!yrs.includes(CURRENT_YEAR)) setYear(yrs[0]);
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/reports2/remittances/monthly?year=${year}&month=${month}`);
      setData(r.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [year, month]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExport = () => {
    if (!data?.data?.length) return;
    const rows = data.data.map((r, i) => ({
      'Sr.No.':       i + 1,
      'Remittance No.': r.remittance_number || '',
      'Date':         r.remittance_date?.substring(0, 10) || '',
      'Completion Date': r.completion_date?.substring(0, 10) || '',
      'Beneficiary':  r.beneficiary_name || '',
      'Currency':     r.currency || 'USD',
      'Amount (USD)': r.amount || 0,
      'Exchange Rate': r.exchange_rate || 0,
      'INR Amount':   r.inr_amount || 0,
      'Bank Ref':     r.bank_reference || '',
      'Kotak Ref':    r.kotak_reference || '',
      'Bank Charges': r.bank_charges || 0,
      'GST on Charges': r.bank_charges_gst || 0,
      'FCC-GST':      r.bank_charges_fcc_gst || 0,
      'Total Charges': r.total_charges || 0,
      'Purpose Code': r.purpose_code || '',
      'Status':       r.status || '',
    }));
    exportExcel(rows, 'Remittances', `Remittance_Report_${MONTH_FULL[month]}_${year}.xlsx`);
  };

  const s   = data?.summary  || {};
  const remRows = data?.data || [];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <MonthYearPicker year={year} month={month} onYear={setYear} onMonth={setMonth} availableYears={availableYears} />
        <Button variant="contained" startIcon={<FileDownload />} onClick={handleExport} disabled={!remRows.length}>
          Export Excel
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : data ? (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <StatCard icon={<Summarize />} label="Total Remittances" value={s.count ?? 0} color="primary.main" />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard icon={<CurrencyExchange />} label="Total USD Sent" value={USD(s.total_usd)} color="success.main" />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard icon={<AccountBalance />} label="Total INR Debit" value={INR(s.total_inr)} color="warning.main" />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard icon={<TrendingUp />} label="Bank Charges" value={INR(s.total_charges)} color="error.main" />
            </Grid>
          </Grid>

          {remRows.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
              <Typography color="text.secondary">No remittances found for {MONTH_FULL[month]} {year}.</Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.50' } }}>
                    <TableCell>#</TableCell>
                    <TableCell>Ref / Date</TableCell>
                    <TableCell>Beneficiary</TableCell>
                    <TableCell align="right">Amount (USD)</TableCell>
                    <TableCell align="right">Rate</TableCell>
                    <TableCell align="right">INR Amount</TableCell>
                    <TableCell align="right">Bank Charges</TableCell>
                    <TableCell align="right">Total Charges</TableCell>
                    <TableCell align="center">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {remRows.map((r, i) => (
                    <TableRow key={r.id} hover sx={{ '&:last-child td': { border: 0 } }}>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>{i + 1}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{r.remittance_number || `REM-${r.id}`}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {fmtDate(r.remittance_date)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>{r.beneficiary_name || '—'}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>{USD(r.amount)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">{r.exchange_rate != null ? parseFloat(r.exchange_rate).toFixed(2) : '—'}</Typography>
                      </TableCell>
                      <TableCell align="right">{INR(r.inr_amount)}</TableCell>
                      <TableCell align="right">
                        {r.bank_charges
                          ? <Tooltip title={`GST: ${INR(r.bank_charges_gst)} | FCC-GST: ${INR(r.bank_charges_fcc_gst)}`}>
                              <span>{INR(r.bank_charges)}</span>
                            </Tooltip>
                          : '—'}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>{INR(r.total_charges)}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={r.status || 'pending'} size="small"
                          color={r.status === 'completed' ? 'success' : r.status === 'failed' ? 'error' : 'default'} />
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: '#e3f2fd', '& td': { fontWeight: 700, borderTop: '2px solid #90caf9' } }}>
                    <TableCell colSpan={3} align="right">TOTAL ({remRows.length} remittances)</TableCell>
                    <TableCell align="right">{USD(s.total_usd)}</TableCell>
                    <TableCell />
                    <TableCell align="right">{INR(s.total_inr)}</TableCell>
                    <TableCell />
                    <TableCell align="right">{INR(s.total_charges)}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      ) : null}
    </Box>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TAB 5 – Remittance Yearly Overview
// ════════════════════════════════════════════════════════════════════════════
function RemittanceYearlyOverview() {
  const [year,    setYear]    = useState(CURRENT_YEAR);
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/reports2/remittances/yearly?year=${year}`);
      setData(r.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [year]);

  useEffect(() => { fetch(); }, [fetch]);

  const handleExport = () => {
    if (!data?.months) return;
    const rows = data.months.filter(m => m.count > 0).map(m => ({
      'Month':          m.month_name,
      'Count':          m.count,
      'Total USD':      m.total_usd || 0,
      'Total INR':      m.total_inr || 0,
      'Bank Charges':   m.total_charges || 0,
    }));
    exportExcel(rows, `Remittances ${year}`, `Remittance_Yearly_${year}.xlsx`);
  };

  const chartData = data?.months?.map(m => ({
    name: m.month_name,
    'INR Amount': Math.round(m.total_inr || 0),
    'USD Amount': Math.round(m.total_usd || 0),
    count: m.count,
  })) || [];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <YearPicker year={year} onYear={setYear} availableYears={data?.available_years} />
        <Button variant="contained" startIcon={<FileDownload />} onClick={handleExport} disabled={!data?.months}>
          Export Excel
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : data ? (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <StatCard icon={<Summarize />} label="Total Remittances" value={data?.totals?.count ?? 0} color="primary.main" />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard icon={<CurrencyExchange />} label="Total USD" value={USD(data?.totals?.total_usd)} color="success.main" />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard icon={<AccountBalance />} label="Total INR" value={INR(data?.totals?.total_inr)} color="warning.main" />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard icon={<TrendingUp />} label="Total Charges" value={INR(data?.totals?.total_charges)} color="error.main" />
            </Grid>
          </Grid>

          <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={700} mb={2}>Monthly Remittances (INR) — {year}</Typography>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis tickFormatter={v => `₹${(v/100000).toFixed(0)}L`} />
                <ReTooltip formatter={(v, name) => [name.includes('INR') ? INR(v) : USD(v), name]} />
                <Bar dataKey="INR Amount" fill="#1976d2" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>

          <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.50' } }}>
                  <TableCell>Month</TableCell>
                  <TableCell align="center">Count</TableCell>
                  <TableCell align="right">Total USD</TableCell>
                  <TableCell align="right">Total INR</TableCell>
                  <TableCell align="right">Bank Charges</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data?.months || []).map(m => (
                  <TableRow key={m.month_num} hover
                    sx={{ opacity: m.count === 0 ? 0.4 : 1, '&:last-child td': { border: 0 } }}>
                    <TableCell>{MONTH_FULL[m.month_num]}</TableCell>
                    <TableCell align="center">
                      {m.count > 0
                        ? <Chip label={m.count} size="small" color="primary" variant="outlined" />
                        : <Typography variant="body2" color="text.disabled">—</Typography>}
                    </TableCell>
                    <TableCell align="right">{m.count > 0 ? USD(m.total_usd) : '—'}</TableCell>
                    <TableCell align="right">{m.count > 0 ? INR(m.total_inr) : '—'}</TableCell>
                    <TableCell align="right">{m.count > 0 ? INR(m.total_charges) : '—'}</TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: '#e3f2fd', '& td': { fontWeight: 700, borderTop: '2px solid #90caf9' } }}>
                  <TableCell>TOTAL {year}</TableCell>
                  <TableCell align="center">{data?.totals?.count ?? 0}</TableCell>
                  <TableCell align="right">{USD(data?.totals?.total_usd)}</TableCell>
                  <TableCell align="right">{INR(data?.totals?.total_inr)}</TableCell>
                  <TableCell align="right">{INR(data?.totals?.total_charges)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : null}
    </Box>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TAB 6 – Executive Dashboard
// ════════════════════════════════════════════════════════════════════════════
const PIE_COLORS = ['#1976d2', '#dc004e', '#388e3c', '#f57c00', '#7b1fa2', '#00796b', '#5d4037', '#455a64'];

function pctDelta(curr, prev) {
  const c = parseFloat(curr) || 0; const p = parseFloat(prev) || 0;
  if (!p) return null;
  return ((c - p) / p) * 100;
}

function DeltaChip({ delta }) {
  if (delta == null) return null;
  const up = delta >= 0;
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, color: up ? 'success.main' : 'error.main', fontSize: 12, fontWeight: 600 }}>
      {up ? <ArrowUpward sx={{ fontSize: 14 }} /> : <ArrowDownward sx={{ fontSize: 14 }} />}
      {Math.abs(delta).toFixed(1)}%
    </Box>
  );
}

function ExecutiveDashboard() {
  const [year, setYear]                       = useState(CURRENT_YEAR);
  const [data, setData]                       = useState(null);
  const [loading, setLoading]                 = useState(false);
  const [availableYears, setAvailableYears]   = useState([]);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/reports2/executive?year=${year}`);
      setData(r.data);
      if (r.data.available_years?.length) setAvailableYears(r.data.available_years);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [year]);
  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  if (!data) return null;
  const k = data.kpis || {};
  const prev = data.prev_year || {};

  return (
    <Box>
      <YearPicker year={year} onYear={setYear} availableYears={availableYears} />

      {/* KPI cards row 1 — imports */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<Inventory2 />} label="Total BOEs" value={k.total_boes ?? 0}
            color="primary.main" sub={`${k.active_suppliers || 0} suppliers`} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<AccountBalance />} label="Imports (Assessable)"
            value={INR(k.total_assessable)} color="warning.main"
            sub={<DeltaChip delta={pctDelta(k.total_assessable, prev.total_assessable)} />} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<TrendingUp />} label="Total IGST" value={INR(k.total_igst)}
            color="error.main" sub={<DeltaChip delta={pctDelta(k.total_igst, prev.total_igst)} />} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<ReceiptIcon />} label="Effective Duty Rate"
            value={k.effective_duty_pct != null ? `${parseFloat(k.effective_duty_pct).toFixed(2)}%` : '—'}
            color="info.main" sub={`Total duty ${INR(k.total_duty)}`} />
        </Grid>
      </Grid>

      {/* KPI cards row 2 — payments + outstanding */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<CurrencyExchange />} label="USD Paid (YTD)"
            value={USD(k.total_usd_paid)} color="success.main"
            sub={`${k.total_remittances || 0} remittances`} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<AccountBalance />} label="INR Debited"
            value={INR(k.total_inr_paid)} color="primary.main"
            sub={`Avg rate ₹${k.avg_usd_rate ? parseFloat(k.avg_usd_rate).toFixed(2) : '—'}`} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<Pending />} label="Outstanding BOEs"
            value={k.pending_boes ?? 0} color="error.main"
            sub={INR(k.pending_inr) + ' pending'} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<TrendingUp />} label="Bank Charges"
            value={INR(k.total_bank_charges)} color="warning.main"
            sub={k.total_inr_paid && k.total_bank_charges
              ? `${(parseFloat(k.total_bank_charges)/parseFloat(k.total_inr_paid)*100).toFixed(3)}% of debit`
              : '—'} />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        {/* YoY trend */}
        <Grid item xs={12} md={8}>
          <Paper elevation={2} sx={{ p: 2, borderRadius: 2, height: 380 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              Imports — Year-over-Year (Assessable Value)
            </Typography>
            <ResponsiveContainer width="100%" height="92%">
              <LineChart data={data.yoy}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month_name" />
                <YAxis tickFormatter={v => `₹${(v/100000).toFixed(0)}L`} />
                <ReTooltip formatter={(v) => INR(v)} />
                <Legend />
                <Line type="monotone" dataKey={year - 2} stroke="#90a4ae" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey={year - 1} stroke="#1976d2" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey={year}     stroke="#dc004e" strokeWidth={3} />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Top 5 suppliers */}
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 2, borderRadius: 2, height: 380 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              Top 5 Suppliers ({year})
            </Typography>
            <ResponsiveContainer width="100%" height="92%">
              <PieChart>
                <Pie data={data.top_suppliers || []}
                  dataKey="total_assessable" nameKey="supplier"
                  innerRadius={50} outerRadius={100} paddingAngle={2}>
                  {(data.top_suppliers || []).map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <ReTooltip formatter={(v, n, p) => [INR(v), p?.payload?.supplier?.slice(0, 30)]} />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TAB 7 – Duty Analytics
// ════════════════════════════════════════════════════════════════════════════
function DutyAnalytics() {
  const [year, setYear]                     = useState(CURRENT_YEAR);
  const [data, setData]                     = useState(null);
  const [loading, setLoading]               = useState(false);
  const [availableYears, setAvailableYears] = useState([]);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/reports2/duty?year=${year}`);
      setData(r.data);
      if (r.data.available_years?.length) setAvailableYears(r.data.available_years);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [year]);
  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  if (!data) return null;

  return (
    <Box>
      <YearPicker year={year} onYear={setYear} availableYears={availableYears} />

      {/* Duty rate trend */}
      <Paper elevation={2} sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
          Effective Duty &amp; IGST Rate — Monthly
        </Typography>
        <Box sx={{ height: 320 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data.months}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month_name" />
              <YAxis yAxisId="left" tickFormatter={v => `₹${(v/100000).toFixed(0)}L`} />
              <YAxis yAxisId="right" orientation="right" tickFormatter={v => `${v}%`} />
              <ReTooltip />
              <Legend />
              <Bar yAxisId="left" dataKey="total_assessable" fill="#90caf9" name="Assessable" />
              <Bar yAxisId="left" dataKey="total_duty"       fill="#ef5350" name="Duty" />
              <Line yAxisId="right" type="monotone" dataKey="duty_rate_pct" stroke="#dc004e" strokeWidth={2} name="Duty %" />
              <Line yAxisId="right" type="monotone" dataKey="igst_rate_pct" stroke="#1976d2" strokeWidth={2} name="IGST %" />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      </Paper>

      <Grid container spacing={2}>
        {/* HSN breakdown */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              HSN Code Breakdown (Top 15)
            </Typography>
            <TableContainer sx={{ maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.50' } }}>
                    <TableCell>HSN</TableCell>
                    <TableCell align="center">Count</TableCell>
                    <TableCell align="right">Assessable</TableCell>
                    <TableCell align="right">Duty</TableCell>
                    <TableCell align="right">Rate %</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.hsn || []).map(r => (
                    <TableRow key={r.hs_code} hover>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{r.hs_code}</TableCell>
                      <TableCell align="center">{r.count}</TableCell>
                      <TableCell align="right">{INR(r.total_assessable)}</TableCell>
                      <TableCell align="right">{INR(r.total_duty)}</TableCell>
                      <TableCell align="right">
                        <Chip label={`${r.duty_rate_pct ?? 0}%`} size="small"
                          color={r.duty_rate_pct > 10 ? 'error' : r.duty_rate_pct > 0 ? 'warning' : 'default'}
                          variant="outlined" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Top duty BOEs */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              Top 10 Highest-Duty BOEs
            </Typography>
            <TableContainer sx={{ maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.50' } }}>
                    <TableCell>BOE</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Supplier</TableCell>
                    <TableCell align="right">Duty</TableCell>
                    <TableCell align="right">Rate %</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.top_duty || []).map((r, i) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{r.boe_number}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{fmtDate(r.boe_date)}</TableCell>
                      <TableCell sx={{ fontSize: 12, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.consignor_name || '—'}
                      </TableCell>
                      <TableCell align="right">{INR(r.duty_amount)}</TableCell>
                      <TableCell align="right">
                        <Chip label={`${r.duty_rate_pct ?? 0}%`} size="small" color="error" variant="outlined" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TAB 8 – Forex Performance
// ════════════════════════════════════════════════════════════════════════════
function ForexPerformance() {
  const [year, setYear]                     = useState(CURRENT_YEAR);
  const [data, setData]                     = useState(null);
  const [loading, setLoading]               = useState(false);
  const [availableYears, setAvailableYears] = useState([]);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/reports2/forex?year=${year}`);
      setData(r.data);
      if (r.data.available_years?.length) setAvailableYears(r.data.available_years);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [year]);
  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  if (!data) return null;

  return (
    <Box>
      <YearPicker year={year} onYear={setYear} availableYears={availableYears} />

      {/* USD rate range trend */}
      <Paper elevation={2} sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
          USD/INR Exchange Rate Achieved — Monthly
        </Typography>
        <Box sx={{ height: 300 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.months}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month_name" />
              <YAxis domain={['dataMin - 1', 'dataMax + 1']} tickFormatter={v => `₹${v}`} />
              <ReTooltip formatter={v => v != null ? `₹${parseFloat(v).toFixed(4)}` : '—'} />
              <Legend />
              <Line type="monotone" dataKey="min_rate" stroke="#388e3c" strokeWidth={1.5} dot name="Best (low)" />
              <Line type="monotone" dataKey="avg_rate" stroke="#1976d2" strokeWidth={2.5}      name="Average" />
              <Line type="monotone" dataKey="max_rate" stroke="#dc004e" strokeWidth={1.5} dot name="Worst (high)" />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Paper>

      <Grid container spacing={2}>
        {/* Charges % trend */}
        <Grid item xs={12} md={7}>
          <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              Bank Charges as % of INR Debit
            </Typography>
            <Box sx={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={data.months}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month_name" />
                  <YAxis yAxisId="left"  tickFormatter={v => `₹${(v/100000).toFixed(0)}L`} />
                  <YAxis yAxisId="right" orientation="right" tickFormatter={v => `${v}%`} />
                  <ReTooltip />
                  <Legend />
                  <Bar yAxisId="left" dataKey="total_charges" fill="#f57c00" name="Charges" />
                  <Line yAxisId="right" type="monotone" dataKey="charges_pct" stroke="#dc004e" strokeWidth={2} name="% of Debit" />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Currency mix */}
        <Grid item xs={12} md={5}>
          <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              Currency Mix (by INR debit)
            </Typography>
            <Box sx={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data.currencies || []}
                    dataKey="total_inr" nameKey="currency"
                    outerRadius={90} label={(e) => `${e.currency}: ${e.count}`}>
                    {(data.currencies || []).map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <ReTooltip formatter={(v) => INR(v)} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Best/Worst rate tables */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} color="success.main" sx={{ mb: 1 }}>
              Best Rates Achieved
            </Typography>
            <Table size="small">
              <TableBody>
                {(data.best_rates || []).map(r => (
                  <TableRow key={r.remittance_number}>
                    <TableCell sx={{ fontSize: 12, fontFamily: 'monospace' }}>{r.remittance_number}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{fmtDate(r.remittance_date)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>₹{parseFloat(r.exchange_rate).toFixed(4)}</TableCell>
                    <TableCell align="right">{USD(r.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} color="error.main" sx={{ mb: 1 }}>
              Worst Rates Paid
            </Typography>
            <Table size="small">
              <TableBody>
                {(data.worst_rates || []).map(r => (
                  <TableRow key={r.remittance_number}>
                    <TableCell sx={{ fontSize: 12, fontFamily: 'monospace' }}>{r.remittance_number}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{fmtDate(r.remittance_date)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>₹{parseFloat(r.exchange_rate).toFixed(4)}</TableCell>
                    <TableCell align="right">{USD(r.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TAB 9 – Outstanding Payables
// ════════════════════════════════════════════════════════════════════════════
function OutstandingPayables() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/reports2/payables`);
      setData(r.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const handleExport = () => {
    if (!data?.data?.length) return;
    const rows = data.data.map((r, i) => ({
      'Sr.No.': i + 1,
      'BOE Number': r.boe_number,
      'BOE Date': fmtDate(r.boe_date),
      'HAWB': r.hawb_number,
      'Supplier': r.supplier,
      'Currency': r.currency,
      'Invoice Value': r.invoice_value,
      'Assessable (INR)': r.assessable_value,
      'Duty (INR)': r.duty_amount,
      'IGST (INR)': r.igst_amount,
      'Status': r.status,
      'Days Outstanding': r.days_outstanding,
    }));
    exportExcel(rows, 'Outstanding Payables', `Outstanding_Payables_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  if (!data) return null;
  const s = data.summary || {};

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          BOEs awaiting payment (status = pending or partial). Aging based on BOE date.
        </Typography>
        <Button variant="contained" startIcon={<FileDownload />} onClick={handleExport}
          disabled={!data?.data?.length}>Export Excel</Button>
      </Box>

      {/* Summary cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<Pending />} label="Pending BOEs" value={s.count ?? 0} color="error.main" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<CurrencyExchange />} label="Pending USD" value={USD(s.total_usd)} color="warning.main" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<AccountBalance />} label="Pending INR" value={INR(s.total_inr)} color="primary.main" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<TrendingUp />} label=">90 days overdue"
            value={s.aging_count?.['91+'] ?? 0} color="error.main"
            sub={INR(s.aging_inr?.['91+'])} />
        </Grid>
      </Grid>

      {/* Aging chart */}
      <Paper elevation={2} sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
          Aging Analysis
        </Typography>
        <Box sx={{ height: 240 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={['0-30', '31-60', '61-90', '91+'].map(k => ({
              bucket: k + ' days',
              count: s.aging_count?.[k] ?? 0,
              value: s.aging_inr?.[k]   ?? 0,
            }))}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="bucket" />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" tickFormatter={v => `₹${(v/100000).toFixed(0)}L`} />
              <ReTooltip formatter={(v, n) => n === 'value' ? INR(v) : v} />
              <Legend />
              <Bar yAxisId="left"  dataKey="count" fill="#1976d2" name="Count" />
              <Bar yAxisId="right" dataKey="value" fill="#ef5350" name="INR Value" />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Paper>

      {/* Detail table */}
      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.50' } }}>
              <TableCell>#</TableCell>
              <TableCell>BOE Number</TableCell>
              <TableCell>BOE Date</TableCell>
              <TableCell>Supplier</TableCell>
              <TableCell align="right">Invoice ({data.data?.[0]?.currency || 'USD'})</TableCell>
              <TableCell align="right">Assessable (INR)</TableCell>
              <TableCell align="right">IGST</TableCell>
              <TableCell align="center">Days</TableCell>
              <TableCell align="center">Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data.data || []).map((r, i) => (
              <TableRow key={r.id} hover>
                <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>{i + 1}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{r.boe_number}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>{fmtDate(r.boe_date)}</TableCell>
                <TableCell sx={{ fontSize: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.supplier || '—'}
                </TableCell>
                <TableCell align="right">{r.currency === 'USD' ? USD(r.invoice_value) : `${r.currency} ${NUM(r.invoice_value)}`}</TableCell>
                <TableCell align="right">{INR(r.assessable_value)}</TableCell>
                <TableCell align="right">{INR(r.igst_amount)}</TableCell>
                <TableCell align="center">
                  <Chip label={r.days_outstanding} size="small"
                    color={r.days_outstanding > 90 ? 'error' : r.days_outstanding > 60 ? 'warning' : 'default'} />
                </TableCell>
                <TableCell align="center">
                  <Chip label={r.status} size="small" color={r.status === 'partial' ? 'warning' : 'error'} variant="outlined" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  ROOT COMPONENT
// ════════════════════════════════════════════════════════════════════════════
const TABS = [
  { label: 'Executive',         icon: <DashboardIcon fontSize="small" />,   component: <ExecutiveDashboard /> },
  { label: 'BOE Monthly',       icon: <CalendarMonth fontSize="small" />,   component: <BOEMonthlyReport /> },
  { label: 'BOE Yearly',        icon: <BarChartIcon fontSize="small" />,    component: <BOEYearlyOverview /> },
  { label: 'Supplier Analysis', icon: <Groups fontSize="small" />,          component: <SupplierAnalysis /> },
  { label: 'Duty Analytics',    icon: <ReceiptIcon fontSize="small" />,     component: <DutyAnalytics /> },
  { label: 'Forex Performance', icon: <ShowChart fontSize="small" />,       component: <ForexPerformance /> },
  { label: 'Outstanding',       icon: <Pending fontSize="small" />,         component: <OutstandingPayables /> },
  { label: 'Remittance Monthly',icon: <TableChart fontSize="small" />,      component: <RemittanceMonthlyReport /> },
  { label: 'Remittance Yearly', icon: <TrendingUp fontSize="small" />,      component: <RemittanceYearlyOverview /> },
];

export default function ReportsNew() {
  const [tab, setTab] = useState(0);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>Reports</Typography>
        <Typography variant="body2" color="text.secondary">
          Financial summaries, duty analysis, and remittance reports — all exportable to Excel.
        </Typography>
      </Box>

      {/* Tab bar */}
      <Paper elevation={2} sx={{ borderRadius: 2, mb: 3 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}
        >
          {TABS.map((t, i) => (
            <Tab key={i} label={t.label} icon={t.icon} iconPosition="start"
              sx={{ textTransform: 'none', fontWeight: 600, minHeight: 52 }} />
          ))}
        </Tabs>
      </Paper>

      {/* Tab content */}
      <TabErrorBoundary key={tab}>
        {TABS[tab].component}
      </TabErrorBoundary>
    </Box>
  );
}
